// utils/commissionCalculator.js
/**
 * Calculate graduated commission like income tax
 * @param {number} sales - Sales amount
 * @param {string} type - 'TM' | 'OV' | 'TL'
 * @returns {number} - Commission amount
 */
export const calcCommission = (sales, type) => {
  if (!sales || sales <= 0) return 0;
  
  const bands = [0, 500, 1000, 1500, 2000, 2500];
  
  const rates = {
    'TM': [0.05, 0.10, 0.15, 0.20, 0.25, 0.30],  // Team Member
    'OV': [0.05, 0.05, 0.05, 0.05, 0.05, 0.05],  // Override
    'TL': [0.10, 0.15, 0.20, 0.25, 0.30, 0.35]   // Team Leader
  };
  
  const rate = rates[type];
  if (!rate) return 0;
  
  let commission = 0;
  
  for (let i = 0; i < bands.length; i++) {
    const nextBand = (i + 1 < bands.length) ? bands[i + 1] : Infinity;
    if (sales > bands[i]) {
      const taxable = Math.min(sales - bands[i], nextBand - bands[i]);
      commission += taxable * rate[i];
    }
  }
  
  return commission;
};

/**
 * Calculate complete team commission report
 * @param {Array} teamMembers - Array of team member objects with sales data
 * @param {number} tlSales - Team Leader's own sales
 * @param {number} tlTarget - Team Leader's target (default: 1000)
 * @returns {Object} - Complete report with all analysis data
 */
export const generateTeamReport = (teamMembers, tlSales, tlTarget = 1000) => {
  // Extract sales from team members
  const teamSales = teamMembers.map(tm => tm.sales || 0);
  
  // Basic counts
  const totalTMs = teamSales.length;
  const activeTMs = teamSales.filter(s => s > 0).length;
  
  // TM calculations
  const tmCommissions = teamSales.map(sales => calcCommission(sales, 'TM'));
  const overridePerTM = teamSales.map(sales => calcCommission(sales, 'OV'));
  
  // Totals
  const totalTeamSales = teamSales.reduce((sum, val) => sum + val, 0);
  const totalTMCommission = tmCommissions.reduce((sum, val) => sum + val, 0);
  const totalOverride = overridePerTM.reduce((sum, val) => sum + val, 0);
  const avgTMSales = totalTMs > 0 ? totalTeamSales / totalTMs : 0;
  const avgTMCommission = totalTMs > 0 ? totalTMCommission / totalTMs : 0;
  
  // TL calculations
  const targetRatio = tlTarget > 0 ? Math.min(tlSales / tlTarget, 1) : 0;
  const overridePaidToTL = totalOverride * targetRatio;
  const tlCommission = calcCommission(tlSales, 'TL');
  
  // Statistics
  const maxTMSales = teamSales.length > 0 ? Math.max(...teamSales, 0) : 0;
  const minTMSales = teamSales.filter(s => s > 0).length > 0 ? Math.min(...teamSales.filter(s => s > 0), 0) : 0;
  const maxTMCommission = tmCommissions.length > 0 ? Math.max(...tmCommissions, 0) : 0;
  
  // Band distribution analysis
  const bands = [0, 500, 1000, 1500, 2000, 2500];
  const bandNames = ['0-500', '500-1000', '1000-1500', '1500-2000', '2000-2500', '2500+'];
  const tmBandDistribution = bandNames.map(() => 0);
  const tmSalesInBands = bandNames.map(() => 0);
  
  teamSales.forEach(sales => {
    if (sales <= 0) return;
    let bandIndex = 0;
    for (let i = bands.length - 1; i >= 0; i--) {
      if (sales > bands[i]) {
        bandIndex = i;
        break;
      }
    }
    tmBandDistribution[bandIndex]++;
    tmSalesInBands[bandIndex] += sales;
  });
  
  return {
    summary: {
      totalTMs: totalTMs,
      activeTMs: activeTMs,
      inactiveTMs: totalTMs - activeTMs,
      totalTeamSales: totalTeamSales,
      totalTMCommission: totalTMCommission,
      totalOverride: totalOverride,
      overridePaidToTL: overridePaidToTL,
      tlCommission: tlCommission,
      grandTotal: totalTMCommission + tlCommission + overridePaidToTL,
      
      avgTMSales: avgTMSales,
      avgTMCommission: avgTMCommission,
      
      tlSales: tlSales,
      tlTarget: tlTarget,
      targetRatio: targetRatio,
      targetAttainment: targetRatio >= 1 ? 'Met' : 'Not Met',
      targetGap: Math.max(0, tlTarget - tlSales),
      
      maxTMSales: maxTMSales,
      minTMSales: minTMSales,
      maxTMCommission: maxTMCommission
    },
    
    bandDistribution: {
      bands: bands,
      bandNames: bandNames,
      tmCount: tmBandDistribution,
      tmSales: tmSalesInBands
    },
    
    teamMembers: teamSales.map((sales, i) => ({
      tmNumber: i + 1,
      sales: sales,
      tmCommission: tmCommissions[i],
      overrideGenerated: overridePerTM[i],
      effectiveRate: sales > 0 ? (tmCommissions[i] / sales * 100) : 0
    })),
    
    overrideBreakdown: {
      totalOverrideGenerated: totalOverride,
      overridePaidToTL: overridePaidToTL,
      overrideWithheld: totalOverride - overridePaidToTL,
      withholdingReason: targetRatio < 1 ? `TL only achieved ${(targetRatio * 100).toFixed(1)}% of target` : 'Full override paid'
    }
  };
};
